<script setup lang="ts">
import { useAuthStorage } from '~/composables/useAuthStorage'
import { useCartorioCor } from '~/composables/useCartorioCor'
import { useNtaskApi } from '~/composables/useNtaskApi'
import { useFormatacao } from '~/composables/useFormatacao'
import type { Cartorio } from '~/types/auth'

const { getCartorios, getCurrentCartorio, getAuthData, saveAuthData } = useAuthStorage()
const { getBgCor } = useCartorioCor()
const { get } = useNtaskApi()
const { formatarCns, formatCpfCnpj } = useFormatacao()

const selectedCartorio = ref(getCurrentCartorio()?.id || null)

const cartoriosOptions = computed(() => {
  const cartorios = getCartorios()
  const currentCartorio = getCurrentCartorio()
  
  return cartorios.map(cartorio => ({
    label: formatarNomeCartorio(cartorio),
    value: cartorio.id,
    cartorio: cartorio,
    isCurrent: currentCartorio?.id === cartorio.id,
    nome: formatarNomeCartorio(cartorio),
    original: cartorio
  }))
})

watch(() => getCurrentCartorio(), (newCartorio) => {
  if (newCartorio) {
    selectedCartorio.value = newCartorio.id
  }
}, { immediate: true })

function formatarNomeCartorio(cartorio: Cartorio) {
  if (!cartorio) return ''

  let prefixo = cartorio.nome_fantasia ? `${cartorio.nome_fantasia} - ` : ''

  if (!cartorio.perfil_organizacao_id || cartorio.cns.length < 11) {
    return `${prefixo}${formatarCns(cartorio.cns)}`
  } else {
    const tipo = cartorio.cns.length === 11 ? 'CPF' : 'CNPJ'
    return `${prefixo}${tipo} ${formatCpfCnpj(cartorio.cns)}`
  }
}

const onCartorioChange = async (event: any) => {
  const selectedOption = event.value
  if (selectedOption) {
    try {
      const response: any = await get(`/autenticacao/selecionar/${selectedOption}`)
      if (response?.token) {
        const authData = getAuthData()
        if (authData) {
          authData.token = response.token
          
          const cartorioSelecionado = authData.cartorios.find(c => c.id === selectedOption)
          if (cartorioSelecionado) {
            authData.cartorio = cartorioSelecionado
          }
          saveAuthData(authData)
        }
      }
      
      window.location.reload()
    } catch (err) {
      console.error('Erro ao selecionar cartorio na API:', err)
    }
  }
}

const usuarioStore = {
  getBgCor
}

</script>

<template>
  <div class="select-cartorio me-auto user-select-none">
    <PDropdown 
      v-model="selectedCartorio" 
      :options="cartoriosOptions" 
      optionLabel="nome"
      optionValue="value"
      placeholder="Selecione um cartório" 
      class="bootstrap-themed-select" 
      filter 
      @change="onCartorioChange"
      appendTo="self">
      <template #value="slotProps">
        <div class="text-white">
          {{
            cartoriosOptions.find(opt => opt.value === slotProps.value)?.nome ||
            'Selecione um cartório'
          }}
        </div>
      </template>

      <template #option="slotProps">
        <div class="d-flex align-items-center my-1">
          <span class="rounded-bullet me-2"
            :style="`background-color: ${usuarioStore.getBgCor(slotProps.option.original.cor)}`"></span>
          <div>{{ slotProps.option.nome }}</div>
        </div>
      </template>
    </PDropdown>
  </div>
</template>

<style>
.select-cartorio {
  position: relative;
  z-index: 1000;
}


.bootstrap-themed-select,
.bootstrap-themed-select .p-dropdown,
.bootstrap-themed-select .p-inputtext {
  border: none !important;
  box-shadow: none !important;
}

.bootstrap-themed-select {
  background-color: transparent !important;
}

.bootstrap-themed-select:hover,
.bootstrap-themed-select:focus-within {
  background-color: rgba(255,255,255,0.08);
  box-shadow: 0 0 0 2px #fff3, 0 2px 8px 0 #0002;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}

.bootstrap-themed-select .p-dropdown:hover,
.bootstrap-themed-select .p-dropdown:focus-within {
  background-color: rgba(255,255,255,0.08);
  box-shadow: 0 0 0 2px #fff3, 0 2px 8px 0 #0002;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}

.p-select-overlay {
  z-index: 1001 !important;
  position: absolute;
  /* width: 100%; */
}

.rounded-bullet {
  width: 1rem;
  height: 1rem;
  display: block;
  border-radius: 50%;
}
</style>